import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { QrCode, CalendarDays, CalendarCheck, Globe, Sparkles, Send } from 'lucide-react';
import { cn } from '@/lib/core/utils';
import { useIsMobile } from '@/hooks/ui/use-mobile';
import { toast } from 'sonner';

interface ServiceCardProps {
  icon: React.ElementType;
  title: string;
  description: string;
  features: string[];
  iconColor: string;
  iconBg: string;
}

const ServiceCard: React.FC<ServiceCardProps> = ({
  icon: Icon,
  title,
  description,
  features,
  iconColor,
  iconBg
}) => {
  return (
    <Card className="h-full hover:shadow-lg transition-shadow">
      <CardHeader className="pb-3">
        <div className={cn("w-12 h-12 rounded-lg flex items-center justify-center mb-3", iconBg)}>
          <Icon className={cn("h-6 w-6", iconColor)} />
        </div>
        <CardTitle className="text-lg">{title}</CardTitle>
        <CardDescription>{description}</CardDescription>
      </CardHeader>
      <CardContent>
        <ul className="space-y-2">
          {features.map((feature, index) => (
            <li key={index} className="flex items-center gap-2 text-sm text-muted-foreground">
              <div className="h-1.5 w-1.5 rounded-full bg-primary" />
              {feature}
            </li>
          ))}
        </ul>
      </CardContent>
    </Card>
  );
};

const services: ServiceCardProps[] = [
  {
    icon: QrCode,
    title: 'Table Ordering',
    description: 'Digital menu and ordering system for your restaurant',
    features: ['QR code menus', 'Real-time order notifications', 'Kitchen display integration', 'Payment processing'],
    iconColor: 'text-blue-600',
    iconBg: 'bg-blue-100 dark:bg-blue-950',
  },
  {
    icon: CalendarDays,
    title: 'Table Reservations',
    description: 'Online booking system for tables',
    features: ['Real-time availability', 'Automated confirmations', 'Waitlist management', 'SMS reminders'],
    iconColor: 'text-green-600',
    iconBg: 'bg-green-100 dark:bg-green-950',
  },
  {
    icon: CalendarCheck,
    title: 'Reservation Systems',
    description: 'Full-featured reservation management',
    features: ['Multi-resource booking', 'Staff scheduling integration', 'Deposit handling', 'Custom booking rules'],
    iconColor: 'text-purple-600',
    iconBg: 'bg-purple-100 dark:bg-purple-950',
  },
  {
    icon: Globe,
    title: 'Website Creation',
    description: 'Custom business websites tailored to your brand',
    features: ['Modern, responsive design', 'Mobile-friendly', 'SEO optimized', 'Easy content management'],
    iconColor: 'text-orange-600',
    iconBg: 'bg-orange-100 dark:bg-orange-950',
  },
];

const CustomSolutionsTab: React.FC = () => {
  const isMobile = useIsMobile();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: '',
    services: [] as string[],
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleServiceToggle = (serviceName: string) => {
    setFormData(prev => ({
      ...prev,
      services: prev.services.includes(serviceName)
        ? prev.services.filter(s => s !== serviceName)
        : [...prev.services, serviceName],
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.name || !formData.email || formData.services.length === 0) {
      toast.error('Please fill in all required fields', {
        description: 'Name, email, and at least one service are required.',
      });
      return;
    }

    setIsSubmitting(true);

    // Simulate submission delay
    await new Promise(resolve => setTimeout(resolve, 500));

    toast.success('Inquiry submitted successfully!', {
      description: 'We will get back to you within 24-48 hours.',
    });

    // Clear form
    setFormData({
      name: '',
      email: '',
      message: '',
      services: [],
    });

    setIsSubmitting(false);
  };

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="space-y-2">
        <h2 className="text-2xl font-bold flex items-center gap-2">
          <Sparkles className="h-6 w-6 text-primary" />
          Custom Solutions
        </h2>
        <p className="text-muted-foreground max-w-2xl">
          Expand your business capabilities with our custom development services.
          We build tailored solutions to help your venues thrive in the digital age.
        </p>
      </div>

      {/* Services Grid */}
      <div className={cn(
        "grid gap-6",
        isMobile ? "grid-cols-1" : "grid-cols-2 lg:grid-cols-4"
      )}>
        {services.map((service, index) => (
          <ServiceCard key={index} {...service} />
        ))}
      </div>

      {/* Contact Form */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Send className="h-5 w-5 text-muted-foreground" />
            Request Information
          </CardTitle>
          <CardDescription>
            Interested in any of our solutions? Fill out the form below and we'll get in touch.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className={cn(
              "grid gap-4",
              isMobile ? "grid-cols-1" : "grid-cols-2"
            )}>
              <div className="space-y-2">
                <Label htmlFor="name">Name *</Label>
                <Input
                  id="name"
                  placeholder="Your name"
                  value={formData.name}
                  onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="email">Email *</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="your@email.com"
                  value={formData.email}
                  onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                />
              </div>
            </div>

            <div className="space-y-3">
              <Label>Services interested in *</Label>
              <div className={cn(
                "grid gap-3",
                isMobile ? "grid-cols-1" : "grid-cols-2"
              )}>
                {services.map((service) => (
                  <div key={service.title} className="flex items-center space-x-2">
                    <Checkbox
                      id={service.title}
                      checked={formData.services.includes(service.title)}
                      onCheckedChange={() => handleServiceToggle(service.title)}
                    />
                    <label
                      htmlFor={service.title}
                      className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer"
                    >
                      {service.title}
                    </label>
                  </div>
                ))}
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="message">Additional details</Label>
              <Textarea
                id="message"
                placeholder="Tell us about your requirements, timeline, or any questions you have..."
                value={formData.message}
                onChange={(e) => setFormData(prev => ({ ...prev, message: e.target.value }))}
                rows={4}
              />
            </div>

            <Button type="submit" disabled={isSubmitting} className="w-full sm:w-auto">
              {isSubmitting ? 'Submitting...' : 'Submit Inquiry'}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default CustomSolutionsTab;
